import {
  useId,
  useMemo,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { cn } from '@/components/ui';
import { WEIGHT_OPTIONS } from '@/logic/weights';
import {
  fitBounds,
  paneHeight,
  project,
  residualPixelsPerUnit,
  zoomAround,
  type Bounds,
  type Insets,
  type Size,
} from './geometry';
import { declutterLabels, type Box, type LabelCandidate } from './labels';
import { MAX_ZOOM, MIN_ZOOM, ZOOM_STEP, type Exaggeration } from './mapDisplay';
import {
  ComparisonLayer,
  GridLayer,
  ResidualLayer,
  UncertaintyLayer,
  type FitMovement,
  type ResidualSegment,
  type UncertaintyCircle,
} from './MapLayers';
import { NorthArrow, PaneLabel, ScaleBar, ZoomControls } from './MapChrome';
import type { MapPoint } from './mapModel';
import { largestMovement } from './geometry';
import { PointLayer, type ProjectedPoint } from './PointLayer';
import { useElementWidth } from './useElementWidth';
import { usePanZoom, type MapViewport } from './useMapViewport';
import { useRovingFocus } from './useRovingFocus';

/** Room kept clear of data for the overlaid label, arrow, scale, and zoom. */
const INSETS: Insets = { top: 48, right: 20, bottom: 52, left: 20 };
const KIND_ORDER = { pair: 0, transformed: 1, unmatched: 2 } as const;

export interface PaneHover {
  point: MapPoint;
  x: number;
  y: number;
}

export interface MapPaneProps {
  /** Accessible name of the pane group, e.g. "Record map". */
  name: string;
  /** Visible label chip content. */
  label: ReactNode;
  bounds: Bounds | null;
  viewport: MapViewport;
  onFit: () => void;
  /** Points in drawing order (later points draw on top). */
  points: readonly MapPoint[];
  selectedPairId: number | null;
  hoveredKey: string | null;
  linkSourceKey: string | null;
  /** Unit label for the scale bar. */
  units?: string;
  /**
   * Shared scale of residual vectors and uncertainty circles: both are
   * exaggerated together so a vector can be compared with its circle.
   */
  vectorScale?: { exaggeration: Exaggeration; reference: number } | null;
  residuals?: readonly ResidualSegment[] | null;
  uncertainty?: readonly UncertaintyCircle[] | null;
  comparison?: {
    movements: readonly FitMovement[];
    referenceLabel: string;
  } | null;
  /** Width assumed before the pane is measured (tests, first paint). */
  defaultWidth?: number;
  emptyMessage?: string;
  onActivate: (point: MapPoint) => void;
  onOpenMenu: (
    point: MapPoint,
    position: { x: number; y: number },
    element: Element
  ) => void;
  onHover: (hover: PaneHover | null) => void;
  onBackgroundClick: () => void;
}

/**
 * One map pane: a fitted, pannable, zoomable SVG with layered geometry,
 * decluttered labels, one keyboard tab stop, and overlaid map controls.
 */
export const MapPane = ({
  name,
  label,
  bounds,
  viewport,
  onFit,
  points,
  selectedPairId,
  hoveredKey,
  linkSourceKey,
  units,
  vectorScale,
  residuals,
  uncertainty,
  comparison,
  defaultWidth = 450,
  emptyMessage = 'No points to display',
  onActivate,
  onOpenMenu,
  onHover,
  onBackgroundClick,
}: MapPaneProps) => {
  const uid = useId().replace(/:/g, '');
  const [wrapRef, measuredWidth] = useElementWidth<HTMLDivElement>();
  const svgRef = useRef<SVGSVGElement>(null);
  const width = measuredWidth || defaultWidth;
  const height = paneHeight(width);
  const size: Size = useMemo(() => ({ width, height }), [width, height]);

  const frame = useMemo(
    () => (bounds ? fitBounds(bounds, size, INSETS) : null),
    [bounds, size]
  );
  const { view } = viewport;
  const projection = useMemo(
    () => (frame ? project(frame, view) : null),
    [frame, view]
  );
  const origin = useMemo(
    () =>
      frame
        ? { x: frame.originX, y: frame.originY }
        : { x: width / 2, y: height / 2 },
    [frame, width, height]
  );
  const gestures = usePanZoom(svgRef, viewport, origin);

  const projected = useMemo<ProjectedPoint[]>(
    () =>
      projection
        ? points.flatMap((point) =>
            Number.isFinite(point.n) && Number.isFinite(point.e)
              ? [{ ...point, ...projection.toXY(point.n, point.e) }]
              : []
          )
        : [],
    [points, projection]
  );
  const navigation = useMemo(
    () =>
      projected
        .map((point, index) => ({ point, index }))
        .sort(
          (a, b) =>
            KIND_ORDER[a.point.kind] - KIND_ORDER[b.point.kind] ||
            a.index - b.index
        )
        .map(({ point }) => point),
    [projected]
  );
  const roving = useRovingFocus<HTMLDivElement>(navigation, selectedPairId);

  const labels = useMemo(() => {
    const reserved: Box[] = [
      { x: 0, y: 0, width: Math.min(260, width * 0.6), height: 40 },
      { x: width - 48, y: 0, width: 48, height: 48 },
      { x: 0, y: height - 58, width: 160, height: 58 },
      { x: width - 176, y: height - 46, width: 176, height: 46 },
    ];
    const candidates: LabelCandidate[] = projected
      .filter((point) => point.kind !== 'transformed')
      .map((point) => {
        const selected =
          point.kind === 'pair' &&
          selectedPairId !== null &&
          point.pairId === selectedPairId;
        // Selection, flag, link, hover, and focus rings need extra room.
        const ringed =
          selected ||
          point.flagged ||
          point.key === linkSourceKey ||
          point.key === hoveredKey;
        return {
          key: point.key,
          x: point.x,
          y: point.y,
          text: point.label,
          priority: point.kind === 'pair' ? (point.flagged ? 3 : 2) : 1,
          force: ringed,
          radius: ringed ? 14 : undefined,
        };
      });
    return declutterLabels(candidates, {
      width,
      height,
      obstacles: projected,
      reserved,
    });
  }, [projected, selectedPairId, hoveredKey, linkSourceKey, width, height]);

  const pixelsPerUnit = projection?.pixelsPerUnit ?? 1;
  const showsResiduals = !!residuals;
  const showsUncertainty = !!uncertainty && uncertainty.length > 0;
  const stretch =
    vectorScale && (showsResiduals || showsUncertainty)
      ? residualPixelsPerUnit(
          vectorScale.exaggeration,
          vectorScale.reference,
          pixelsPerUnit,
          size
        ) / pixelsPerUnit
      : null;
  const vectorLabel =
    showsResiduals && showsUncertainty
      ? 'Residuals & circles'
      : showsResiduals
        ? 'Residuals'
        : '95% circles';

  const zoomBy = (factor: number) =>
    viewport.update((current) => zoomAround(current, factor, origin, origin), {
      immediate: true,
    });

  const handlePaneKeyDown = (event: ReactKeyboardEvent) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    if (
      event.target instanceof HTMLElement &&
      event.target.closest('input,textarea,select,[contenteditable="true"]')
    ) {
      return;
    }
    if (event.key === '+' || event.key === '=') zoomBy(ZOOM_STEP);
    else if (event.key === '-' || event.key === '_') zoomBy(1 / ZOOM_STEP);
    else if (event.key === '0') onFit();
    else return;
    event.preventDefault();
  };

  const describedBy = (point: MapPoint) => {
    const ids = [
      point.status ? `${uid}-weight-${point.status}` : `${uid}-unmatched`,
      point.flagged ? `${uid}-flagged` : null,
    ].filter(Boolean);
    return ids.join(' ') || undefined;
  };

  return (
    <div
      ref={roving.containerRef}
      role="group"
      aria-label={name}
      onKeyDown={handlePaneKeyDown}
      className="min-w-0"
    >
      <div
        ref={wrapRef}
        className="relative overflow-hidden rounded-lg border border-border bg-surface-sunken"
      >
        <svg
          ref={svgRef}
          width={width}
          height={height}
          className={cn(
            'block touch-none select-none',
            gestures.panning ? 'cursor-grabbing' : 'cursor-grab'
          )}
          onClick={onBackgroundClick}
          {...gestures.handlers}
        >
          {projection ? (
            <>
              <GridLayer projection={projection} size={size} />
              {uncertainty && stretch !== null && (
                <UncertaintyLayer
                  circles={uncertainty}
                  projection={projection}
                  radiusScale={stretch * pixelsPerUnit}
                />
              )}
              {comparison && (
                <ComparisonLayer
                  movements={comparison.movements}
                  projection={projection}
                  referenceLabel={comparison.referenceLabel}
                />
              )}
              {residuals && stretch !== null && (
                <ResidualLayer
                  segments={residuals}
                  projection={projection}
                  stretch={stretch}
                  markerId={`${uid}-arrow`}
                />
              )}
              <PointLayer
                points={projected}
                labels={labels}
                selectedPairId={selectedPairId}
                hoveredKey={hoveredKey}
                linkSourceKey={linkSourceKey}
                tabStop={roving.tabStop}
                describedBy={describedBy}
                onActivate={onActivate}
                onOpenMenu={onOpenMenu}
                onHover={(point, position) =>
                  onHover(
                    point && position
                      ? { point, x: position.x, y: position.y }
                      : null
                  )
                }
                onFocusPoint={(point) => roving.onFocus(point.key)}
                onKeyDown={(event, point) => roving.onKeyDown(event, point.key)}
              />
            </>
          ) : (
            <text
              x={width / 2}
              y={height / 2}
              textAnchor="middle"
              className="fill-fg-muted text-sm"
            >
              {emptyMessage}
            </text>
          )}
        </svg>

        <PaneLabel>{label}</PaneLabel>
        <NorthArrow />
        {projection && (
          <>
            <ScaleBar
              pixelsPerUnit={pixelsPerUnit}
              paneWidth={width}
              units={units}
              vectorMultiplier={stretch}
              vectorLabel={vectorLabel}
              largestMovement={
                comparison ? largestMovement(comparison.movements) : null
              }
            />
            <ZoomControls
              zoom={view.zoom}
              canZoomIn={view.zoom < MAX_ZOOM}
              canZoomOut={view.zoom > MIN_ZOOM}
              onZoomIn={() => zoomBy(ZOOM_STEP)}
              onZoomOut={() => zoomBy(1 / ZOOM_STEP)}
              onFit={onFit}
            />
          </>
        )}
      </div>
      <span hidden>
        {WEIGHT_OPTIONS.map((option) => (
          <span key={option.status} id={`${uid}-weight-${option.status}`}>
            {option.label} weight
          </span>
        ))}
        <span id={`${uid}-unmatched`}>Not paired</span>
        <span id={`${uid}-flagged`}>Flagged for review</span>
      </span>
    </div>
  );
};
